import React from 'react';
import moment from 'moment';
import lodash from 'lodash';
import ModelProxy, {required, optional} from './proxy';
import Reference from './reference';
import RawRxHistory from './raw-rx-history';
import RawSuscResult from './raw-susc-result';
import RawSequenceData from './raw-sequence-data';
import RawAlignment from './raw-alignment';
import ExportedTableRow from './exported-table-row';
import Patient from './patient';
import Isolate from './isolate';
import {
  dateStringSort,
  localeSort
} from '../utils/sort';

import {
  ALL_GENES, GENE_SIZES,
  NRTI_NNRTI, PI, INSTI, CAI
} from '../constants';

import {
  getDRMutations,
} from '../utils/QA';

const DRUG_INHIBITORS = {
  'PR': PI,
  'RT': NRTI_NNRTI,
  'IN': INSTI,
  'CA': CAI,
};

const DTFORMAT = 'YYYY-MM-DD';


function addIsolateSequence(isolate, accession, seqType, cloneName, geneSeq) {
  const {firstAA, lastAA, alignedNAs, alignedAAs, mutations} = geneSeq;
  const insertions = [];
  const mixtures = [];
  const hasNAs = Boolean(alignedNAs);
  for (const {position, AAs, insertedNAs, isInsertion} of mutations) {
    if (isInsertion) {
      const insertedAAs = AAs.split('_', 2)[1];
      insertions.push({
        codonPosition: position,
        aa: insertedAAs,
        na: insertedNAs
      });
    }
    else if (!hasNAs && AAs.length > 1) {
      // mixtures are only for phenotype sequences
      mixtures.push({
        codonPosition: position,
        aa: AAs
      });
    }
  }
  const sequence = {
    accession, isolateUuid: isolate.uuid,
    sequenceType: seqType, cloneName,
    firstAa: firstAA, lastAa: lastAA,
    naseq: alignedNAs, aaseq: alignedAAs,
    insertions, mixtures
  };
  isolate.sequences = isolate.sequences.concat([sequence]);
}

function simplifySubtype(subtype, species) {
  if (species === 'HIV2') {
    return subtype;
  }
  if (subtype === null) {
    return 'Unknown';
  }
  const ucSubtype = subtype.toUpperCase();
  if (/^GROUP /.test(ucSubtype)) {
    return subtype.slice(6);
  }
  else if (ucSubtype === 'F2') {
    return 'F';
  }
  else if (ucSubtype === 'A (A_FSU)') {
    return 'A';
  }
  else if (ucSubtype === 'A2') {
    return 'A';
  }
  return subtype;
}


export default class RawDataCollection extends ModelProxy {

  static propTypes = {
    references: required,
    sequenceType: required,
    rawSequences: required,
    rawRxHistory: required,
    rawAlignments: required,
    rawSuscResults: required,
    validDrugs: required,
    validCountries: required,
    validBodySources: required,
    loadFrom: optional,
    spreadsheet_id: required,
    sheet_id: required,
  };

  constructor(data) {
    data = Object.assign({
      references: [],
      rawSequences: [],
      rawRxHistory: [],
      rawAlignments: [],
      rawSuscResults: [],
      validDrugs: {},
      validCountries: [],
      validBodySources: [],
      spreadsheet_id: '',
      sheet_id: 0,
    }, data);
    const self = super(data);
    this._caches = {};
    return self;
  }

  static getReference(self) {

    const references = self.props.references;

    let reference = null;
    if (references.length > 0) {
      reference = references[0];
    }
    return reference;
  }

  static setReference(self, ref) {
    this.setReferences(self, [ref]);
  }

  static setReferences(self, refs) {
    self._caches = {};
    self.props.references = refs.map(
      ref => (ref instanceof Reference ?
        ref : new Reference(ref))
    );
  }

  static getHasRawSequences(self) {
    return self.props.rawSequences.length > 0;
  }

  static getHasRawAlignments(self) {
    return self.props.rawAlignments.length > 0;
  }

  static getHasRawRxHistory(self) {
    return self.props.rawRxHistory.length > 0;
  }

  static getCheckRxHistoryPatient(self) {
    const rawRxHistory = self.props.rawRxHistory;
    const rawSeq = self.props.rawSequences;
    const rxPatients = rawRxHistory.map((rx) => rx.ptAlias);
    const seqPatients = rawSeq.map((seq) => seq.ptAlias);
    for (const patient of rxPatients) {
      if (!seqPatients.includes(patient)) {
        return false;
      }
    }
    return true;
  }

  static getHasRawSuscResults(self) {
    return self.props.rawSuscResults.length > 0;
  }

  static getHasCloneSequence(self) {
    const {rawSequences} = self.props;
    let hasCloneSequence = false;
    for (const rawSeq of rawSequences) {
      if (rawSeq.cloneName && rawSeq.cloneName !== '') {
        hasCloneSequence = true;
        break;
      }
    }
    return hasCloneSequence;
  }

  static getHasSimpleRawRxHistory(self) {
    const {rawRxHistory} = self.props;
    let patients = rawRxHistory.map(({ptAlias}) => (ptAlias));
    patients = new Set(patients);

    if (patients.size !== rawRxHistory.length) {
      return false;
    } else {
      return true;
    }
  }

  static getIsolateStartDate(self) {
    const {rawSequences} = self.props;

    const isolateDates = rawSequences.map(
      ({isolateDate}) => (isolateDate)).filter(
      (isolateDate) => (!!isolateDate));

    if (isolateDates.length === 0) {
      return '';
    }

    isolateDates.sort(dateStringSort);
    return isolateDates[0];
  }

  static getIsolateStopDate(self) {
    const {rawSequences} = self.props;

    const isolateDates = rawSequences.map(
      ({isolateDate}) => (isolateDate)).filter(
      (isolateDate) => (!!isolateDate));

    if (isolateDates.length === 0) {
      return '';
    }

    isolateDates.sort(dateStringSort);
    return isolateDates[0];
  }

  static setRawSequences(self, seqs) {
    self._caches = {};
    let rawSequences = seqs.map(
      seq => (seq instanceof RawSequenceData ?
        seq : new RawSequenceData(seq))
    );
    rawSequences.sort((seq1, seq2) => {
      if (seq1.ptAlias && seq2.ptAlias) {
        if (seq1.ptAlias === seq2.ptAlias) {
          return dateStringSort(seq1.isolateDate, seq2.isolateDate);
        } else {
          return localeSort(seq1.ptAlias, seq2.ptAlias);
        }
      }

      return localeSort(seq1.isolateName, seq2.isolateName);
    });

    self.props.rawSequences = rawSequences;
  }

  static getHasClinicalIsolate(self) {
    return self.props.rawSequences.some(({isolateType}) => isolateType === 'Clinical');
  }

  static getHasLabIsolate(self) {
    return self.props.rawSequences.some(({isolateType}) => isolateType === 'Lab');
  }

  static getAccessions(self) {
    return Array.from(
      new Set(
        self.props.rawSequences.map(({accession}) => accession)
      )
    );
  }

  static getHasAllSeqMeta(self) {
    for (const [key, value] of Object.entries(RawSequenceData.propTypes)) {
      if (!value) {
        continue;
      }
      if (self.props.rawSequences.filter((seq) => !seq[key]).length > 0) {
        return false;
      }
    }
    return true;
  }

  static getRegions(self) {
    return Array.from(
      new Set(
        self.props.rawSequences.map(({region}) => region)
          .filter(region => region !== null)
      )
    );
  }

  static getSources(self) {
    return Array.from(
      new Set(
        self.props.rawSequences.map(({source}) => source)
          .filter(source => source !== null)
      )
    );
  }

  static getRawSequenceMap(self) {
    const map = {};
    for (const seq of self.props.rawSequences) {
      const {uuid} = seq;
      map[uuid] = seq;
    }
    return map;
  }

  static getPtAliasIsolateDateMap(self) {
    const map = {};
    for (const seq of self.props.rawSequences) {
      const {ptAlias, isolateDate} = seq;
      map[ptAlias] = map[ptAlias] || new Set();
      if (!map[ptAlias].has(isolateDate)) {
        map[ptAlias].add(isolateDate);
      }
    }

    for (const ptAlias of Object.keys(map)) {
      map[ptAlias] = Array.from(map[ptAlias]).sort();
    }

    return map;
  }

  static getPatientHistoryMap(self) {
    const rawRxHistory = self.props.rawRxHistory;
    const map = {};
    for (const hist of rawRxHistory) {
      const {ptAlias} = hist;
      map[ptAlias] = hist;
    }
    return map;
  }

  static getAllAlignedGeneSequences(self) {
    const allAlignedNAs = ALL_GENES.reduce(
      (acc, gene) => {
        acc[gene] = [];
        return acc;
      }, {}
    );
    const {rawAlignments} = self.props;
    const rsMap = this.getRawSequenceMap(self);
    for (const {rawSequenceUuid, geneSequences} of rawAlignments) {
      const {accession, ptAlias} = rsMap[rawSequenceUuid];
      for (const {gene, firstAA, alignedNAs} of geneSequences) {
        const geneSize = 3 * GENE_SIZES[gene];
        const prefix = new Array(3 * firstAA - 3).fill('.').join('');
        const suffix = new Array(
          geneSize - prefix.length - alignedNAs.length
        ).fill('.').join('');
        const fullAlignedNAs = prefix + alignedNAs + suffix;
        allAlignedNAs[gene].push({
          header: `${ptAlias}-${accession}`,
          sequence: fullAlignedNAs
        });
      }
    }
    for (const [gene, seqs] of Object.entries(allAlignedNAs)) {
      if (seqs.length === 0) {
        delete allAlignedNAs[gene];
      }
    }
    return allAlignedNAs;
  }

  static setRawRxHistory(self, rxes) {
    self._caches = {};
    let rawRxHistory = rxes.map(
      rx => (rx instanceof RawRxHistory ?
        rx : new RawRxHistory(rx))
    );

    rawRxHistory.sort((seq1, seq2) => {
      if (seq1.ptAlias && seq2.ptAlias) {
        if (seq1.ptAlias === seq2.ptAlias) {
          return dateStringSort(seq1.isolateDate, seq2.isolateDate);
        } else {
          return localeSort(seq1.ptAlias, seq2.ptAlias);
        }
      } else {
        return 0;
      }
    });
    self.props.rawRxHistory = rawRxHistory;
  }

  static setRawSuscResults(self, results) {
    self._caches = {};
    let {validDrugs} = self.props;
    validDrugs = validDrugs || {};
    self.props.rawSuscResults = results.map(
      r => {
        r = r instanceof RawSuscResult ? r : new RawSuscResult(r);
        r.drugClass = validDrugs[r.drugName];
        return r;
      }
    );
  }

  static setValidDrugs(self, validDrugs) {
    self._caches = {};
    for (const susc of self.props.rawSuscResults) {
      susc.drugClass = validDrugs[susc.drugName];
    }
    self.props.validDrugs = validDrugs;
  }

  static setRawAlignments(self, alignments) {
    self._caches = {};
    const rawSequenceMap = this.getRawSequenceMap(self);
    self.props.rawAlignments = alignments.map(
      am => (am instanceof RawAlignment ?
        am : new RawAlignment(am))
    ).sort((align1, align2) => {
      const seq1 = rawSequenceMap[align1.rawSequenceUuid];
      const seq2 = rawSequenceMap[align2.rawSequenceUuid];

      if (seq1.isolateName === seq2.isolateName) {
        return 0;
      } else {
        return seq1.isolateName < seq2.isolateName ? -1: 1;
      }
    });
  }

  static getRawSequenceAlignmentPairs(self) {
    const {rawSequences} = self.props;
    const rawAlignmentMap = this._getRawAlignmentMap(self);
    const result = [];
    for (const seq of rawSequences) {
      result.push([seq, rawAlignmentMap[seq.uuid]]);
    }
    return result;
  }

  static validateRawSequences(self, seqs) {
    let msgs = {};
    let validateController = {
      region: false,
      source: false,
    };

    const isolatesClones = {};
    const reversedIsolateDates = {};
    const patientDateIsolateMap = {};
    const refNames = this._getRefNameUuidMap(self);
    const validCountries = new Set(self.props.validCountries);
    const validBodySources = new Set(self.props.validBodySources);
    const rawAlignmentMap = this._getRawAlignmentMap(self);
    const redundantMutationGenes = new Set();
    const check_isolateName_Date = {};

    for (let {
      uuid, refName, ptAlias, isolateName, mutationGenes, region, seqType,
      isolateDate, isolateType, cloneMethod, cloneName, source, seqMethod
    } of seqs) {
      cloneName = (cloneName || '').trim();

      const patientIsolateName = `${ptAlias}-${isolateName}`;
      let patientDateSource = `${ptAlias}-date${isolateDate}-src${source}`;
      const patientDateIsolate = `${ptAlias}-date${isolateDate}-${isolateName}`;
      const patientDateSourceIsolate = `${ptAlias}-date${isolateDate}-src${source}-${isolateName}`;

      if (seqMethod) {
        patientDateSource = patientDateSource + `-method${seqMethod}`;
      }

      const patientDateSourceSeqMethod_NoCloneName = patientDateSource;
      if (cloneMethod != 'None') {
        patientDateSource = patientDateSource + `-clone${cloneName}`;
      }

      const clones = isolatesClones[patientIsolateName] || new Set();
      const rawAlignment = rawAlignmentMap[uuid];

      check_isolateName_Date[patientIsolateName] = check_isolateName_Date[patientIsolateName] || patientDateIsolate;

      if (check_isolateName_Date[patientIsolateName] !== patientDateIsolate) {
        msgs[`${patientIsolateName}1`] = {
          text: (
            `Patient ${ptAlias} has isolates from different dates using same isolate name ${isolateName}.
            Please use different isolate names. One of the date is ${isolateDate}.`
          ),
          level: 'error'
        };
      }

      patientDateIsolateMap[patientDateIsolate] = patientDateIsolateMap[patientDateIsolate] || patientDateSourceSeqMethod_NoCloneName;

      if (patientDateIsolateMap[patientDateIsolate] !== patientDateSourceSeqMethod_NoCloneName) {
        msgs[`${patientDateIsolate}1`] = {
          text: (
            `At ${isolateDate}, patient ${ptAlias} has multiple isolates from different body source or sequencing method, ` +
            `which share isolate name ${isolateName}. Please use different isolate names.`
          ),
          level: 'error'
        };
      }
      if (isolateType === 'Clinical' && mutationGenes && mutationGenes.length > 0) {
        // incomplete sequence
        for (const gene of mutationGenes) {
          const patientDateSourceIsolateGene = `${patientDateSourceIsolate}-${gene}`;
          if (redundantMutationGenes.has(patientDateSourceIsolateGene) && !cloneName) {
            msgs[`${patientDateSourceIsolateGene}`] = {
              text: (
                `At ${isolateDate}, patient ${ptAlias} has multiple isolates from source ${source} with same gene ${gene}, ` +
                `which share same isolate name ${isolateName}. Please use different isolate names.`
              ),
              // `Isolate ${ptAlias}-date${isolateDate}-src${source} has multiple records having ` +
              // `"${gene}" gene.`),
              level: 'error'
            };
          }
          redundantMutationGenes.add(patientDateSourceIsolateGene);
        }
      }
      else if (rawAlignment && ptAlias) {
        // complete sequence
        reversedIsolateDates[patientDateSourceIsolate] = reversedIsolateDates[patientDateSourceIsolate] || new Set();
        const knownGenes = reversedIsolateDates[patientDateSourceIsolate];
        for (const geneSeq of rawAlignment.geneSequences) {
          if (knownGenes.has(geneSeq.gene) && !cloneName) {
            msgs[`${patientDateSourceIsolate}0`] = {
              text: (
                `At ${isolateDate}, patient ${ptAlias} has multiple isolates from source ${source} with same gene ${geneSeq.gene}, ` +
                `which share same isolate name ${isolateName}. Please use different isolate names.`
              ),
              // `Isolate ${isolateDate}/${source}/${isolateName} of patient ${ptAlias} has ` +
              // `multiple records having "${geneSeq.gene}" gene.`),
              level: 'error'
            };
          }
          knownGenes.add(geneSeq.gene);

          // if (!hasQAIssue && testHasQAIssue(geneSeq)) {
          //   msgs[`${patientDateSourceIsolate}1`] = {
          //     text: (
          //       `QA issues are founded, please see table below.`),
          //     level: 'error',
          //   };
          //   hasQAIssue = true;
          // }
        }
        if (rawAlignment.validationResults.length > 0) {
          const vr = rawAlignment.validationResults;
          for (let i = 0; i < vr.length; i ++) {
            let level = 'info';
            if (vr[i].startsWith('SEVERE_WARNING')) {
              level = 'warning';
            }
            msgs[`${patientDateSource}-v${i}`] = {
              text: `Found sequence validation issue for ${patientDateSource}. (${vr[i]})`,
              level: level
            };
          }
        }
      }
      if (cloneName) {
        if (clones.has(cloneName)) {
          msgs[`${patientIsolateName}2`] = {
            text: (
              `Isolate ${patientIsolateName} contains ` +
              `more than 1 clone named "${cloneName}".`
            ),
            level: 'error'
          };
        }
        if (seqType === 'Consensus') {
          msgs[`${patientIsolateName}3`] = {
            text: (
              `The consensus of isolate ${patientIsolateName} should not include any clone name`
            ),
            level: 'error'
          };
        }
      }
      if (seqType !== 'Consensus') {
        clones.add(cloneName);
      }
      isolatesClones[patientIsolateName] = clones;
      if (!refName.startsWith('$$') && !(refName in refNames)) {
        msgs[`${patientIsolateName}4`] = {
          text: (
            `Isolate ${patientIsolateName} is linked to an invalid reference ("${refName || ''}"). ` +
            `Valid value(s) of property "refName" is (are) "${Object.keys(refNames).join('", "')}".`
          ),
          level: 'error'
        };
      }

      if (!validateController.region && region !== null && !validCountries.has((region || '').trim())) {
        msgs[`${patientIsolateName}5`] = {
          text: (
            <span>
              Region "{region}" is not a valid region name.{' '} If you can't find in the dropdown list,
              please send a email to <a href="mailto:hivdbteam@stanford.edu">HIVDB team</a>.
            </span>
          ),
          level: 'error'
        };
        validateController.region = true;
      }

      if (!validateController.source && isolateType === 'Clinical' && source !== null &&
          !validBodySources.has((source || '').trim())) {
        msgs[`${patientIsolateName}6`] = {
          text: (
            <span>
              Source "{source}" is not a valid body source.  If you can't find in the dropdown list,
              please send a email to <a href="mailto:hivdbteam@stanford.edu">HIVDB team</a>.
            </span>
          ),
          level: 'error'
        };
        validateController.source = true;
      }

      // if (isolateNames.includes(isolateName)) {
      //   msgs[`${patientIsolateName}9`] = {
      //     text: (
      //       `Duplicated isolateName found ${isolateName}, please use unique isolateName.`
      //     ),
      //     level: 'error'
      //   };
      // } else {
      //   isolateNames.push(isolateName);
      // }

    }
    for (const [patientDateSource, knownGenes] of Object.entries(reversedIsolateDates)) {
      if (knownGenes.size === 0) {
        msgs[`${patientDateSource}1`] = {
          text: (
            `No PR/RT/IN genes were found from isolate ${patientDateSource}.`),
          level: 'error'
        };
      }
    }
    for (const [patientIsolateName, clones] of Object.entries(isolatesClones)) {
      const isClone = clones.size > 1;
      if (isClone && clones.has('')) {
        msgs[`${patientIsolateName}7`] = {
          text: (
            `Isolate ${patientIsolateName} seems to contain clones, however ` +
            'the property "cloneName" of at least one of the sequences is empty.'
          ),
          level: 'error'
        };
      }
      if (!isClone && !clones.has('')) {
        msgs[`${patientIsolateName}8`] = {
          text: (
            `Isolate ${patientIsolateName} is not a clone but with ` +
            'the property "cloneName" specified, which will be ignored later.'
          ),
          level: 'warning'
        };
      }
    }
    msgs = Object.values(msgs);

    const childMsgs = seqs.reduce((acc, seq) => {
      const {accession} = seq;
      seq.validate();
      const msgs = seq.messages();
      return acc.concat(
        msgs.map(({text, type, group_text, property, level}) => ({
          text: `(${accession}) ${text}`,
          type,
          group_text,
          property,
          level
        }))
      );
    }, []);
    return msgs.concat(childMsgs);
  }

  static validateRawRxHistory(self, rxes) {
    const msgs = [];
    const patientsDates = {};
    const validDrugs = new Set(Object.keys(self.props.validDrugs));
    const ptAliasList = new Set(self.rawSequences.map((seq) => seq.ptAlias));
    const rxPtAliasList = new Set(self.rawRxHistory.map((rx) => rx.ptAlias));

    const missingPtAlias = lodash.difference(
      Array.from(ptAliasList),
      Array.from(rxPtAliasList)
    );

    const validateController = {
      hasDrugError: false,
      hasPtAliasError: false,
    };
    for (let {ptAlias, startDate, stopDate, drugs} of rxes) {
      if (!ptAliasList.has(ptAlias)) {
        msgs.push({
          text: `Patient(${ptAlias}) is not found in sequences`,
          level: 'error'
        });
        validateController.hasPtAliasError = true;
      }
      const dates = patientsDates[ptAlias] || [];
      startDate = moment.utc(startDate);
      stopDate = moment.utc(stopDate);
      for (const pair of dates) {
        const [begin, end] = pair;
        if (
          (startDate.isBefore(begin) && stopDate.isAfter(begin)) ||
          (startDate.isBefore(end) && stopDate.isAfter(end))
        ) {
          msgs.push({
            text: (
              `(${ptAlias}) A date overlap was detected between ` +
              `${begin.format(DTFORMAT)}/${end.format(DTFORMAT)} and ` +
              `${startDate.format(DTFORMAT)}/${stopDate.format(DTFORMAT)}.`
            ),
            level: 'error'
          });
        }
      }
      for (const drug of drugs.split(',')) {
        if (!drug.trim()) {
          if (!validateController.hasDrugError) {
            msgs.push({
              text: `Some regimen history records don't have drugs.`,
              level: 'error'
            });
            validateController.hasDrugError = true;
          }
        } else if (!validDrugs.has(drug.trim())) {
          msgs.push({
            text: (
              <span>
                ({ptAlias}) Drug "{drug}" is not a valid drug name.{' '}
                If it's a new drug, please send a email to <a href="mailto:hivdbteam@stanford.edu">HIVDB team</a>.
              </span>
            ),
            level: 'error'
          });
        }
      }
      dates.push([startDate, stopDate]);
      patientsDates[ptAlias] = dates;
    }
    for (let [ptAlias, dates] of Object.entries(patientsDates)) {
      dates = dates.sort(([start1], [start2]) => start1.diff(start2));
      let prevStart, prevStop;
      for (const [start, stop] of dates) {
        if (prevStop && start.diff(prevStop, 'days') > 0) {
          msgs.push({
            text: (
              `(${ptAlias}) A date gap was detected between ` +
              `${prevStart.format(DTFORMAT)}/${prevStop.format(DTFORMAT)} and ` +
              `${start.format(DTFORMAT)}/${stop.format(DTFORMAT)}.`
            ),
            level: 'error'
          });
        }
        prevStart = start;
        prevStop = stop;
      }
    }

    if (!validateController.hasDrugError && !validateController.hasPtAliasError) {
      if (missingPtAlias.length > 0) {
        msgs.push({
          text: `Patient ${missingPtAlias.join(',')} don't have regimen history`,
          level: 'error',
        });
      }
    }

    const childMsgs = rxes.reduce((acc, rx) => {
      const {ptAlias, startDate} = rx;
      let uniqName = null;
      if (startDate) {
        uniqName = `${ptAlias}-${startDate}`;
      } else {
        uniqName = `${ptAlias}`;
      }
      rx.validate();
      const msgs = rx.messages();
      return acc.concat(
        msgs.map(({text, level}) => ({
          text: `(${uniqName}) ${text}`,
          level
        }))
      );
    }, []);
    return msgs.concat(childMsgs);
  }

  static validateRawSuscResults(self, suscResults) {
    const msgs = [];
    const rawSequenceAlignmentPairs = this.getRawSequenceAlignmentPairs(self);
    const geneKeys = rawSequenceAlignmentPairs.reduce(
      (acc, [{ptAlias, isolateName, mutationGenes}, align]) => {
        if (align && 'geneSequences' in align) {
          for (const {gene} of align.geneSequences) {
            acc.add(`${ptAlias}$$${isolateName}$$${gene}`);
          }
        }
        else {
          for (const gene of mutationGenes) {
            acc.add(`${ptAlias}$$${isolateName}$$${gene}`);
          }
        }
        return acc;
      }, new Set());
    const validDrugs = new Set(Object.keys(self.props.validDrugs));
    const redundantDrugs = new Set();
    for (let {ptAlias, isolateName, drugName: drug, gene, method} of suscResults) {
      const drugKey = `${ptAlias}$$${isolateName}$$${drug}$${method}`;
      const geneKey = `${ptAlias}$$${isolateName}$$${gene}`;

      if (drug && !validDrugs.has(drug)) {
        msgs.push({
          text: (
            <span>
              ({ptAlias}-{isolateName}) Drug "{drug}" is not a valid drug name.{' '}
              If it's a new drug, please send a email to <a href="mailto:hivdbteam@stanford.edu">HIVDB team</a>.
            </span>
          ),
          level: 'error'
        });
      }

      if (drug && !DRUG_INHIBITORS[gene].includes(drug)) {
        msgs.push({
          text: `"${drug}" is not valid for Patient ${ptAlias}, ${gene} isolate ${isolateName}.`,
          level: 'error'
        });
      }

      if (redundantDrugs.has(drugKey)) {
        msgs.push({
          text: `(${ptAlias}-${isolateName}) Found redundant drug: ${drug}.`,
          level: 'error'
        });
      }
      if (drug && gene && !geneKeys.has(geneKey)) {
        msgs.push({
          text: (
            `(${ptAlias}-${isolateName}) Drug "${drug}" applies to ` +
            `gene "${gene}". However the input isolate doesn't contain ` +
            'any mutations from this gene region. If this is intended ' +
            'to be, please fill the coressponding "Gene" field with an ' +
            'existing isolate gene.'),
          level: 'error'
        });
      }
      redundantDrugs.add(drugKey);
    }

    const childMsgs = suscResults.reduce((acc, susc) => {
      const {ptAlias, isolateName} = susc;
      const uniqName = `${ptAlias}-${isolateName}`;
      susc.validate();
      const msgs = susc.messages();
      return acc.concat(
        msgs.map(({text, level}) => ({
          text: `(${uniqName}) ${text}`,
          level
        }))
      );
    }, []);
    return msgs.concat(childMsgs);
  }

  static getPatientList(self) {
    if (!self._caches.patients || self._caches.patients.length === 0) {
      let patients = {};
      const {rawSequences, rawRxHistory} = self.props;
      for (const rawSeq of rawSequences) {
        let {existingPatientId, ptAlias, region, isChild} = rawSeq;
        if (!ptAlias) {
          continue;
        }
        if (patients[ptAlias]) {
          continue;
        }
        isChild = isChild === 'Yes';
        patients[ptAlias] = new Patient({
          id: existingPatientId || null,
          pseudoName: ptAlias, isChild, region
        });
      }
      if (rawRxHistory) {
        for (const rawRx of rawRxHistory) {
          const {
            ptAlias, startDateMatch, startDate,
            stopDate, weeks, orderUnknown, durationUnknown, drugs
          } = rawRx;
          const patient = patients[ptAlias];
          if (!patient) {
            continue;
          }
          const patientUuid = patient.uuid;
          const rx = {
            patientUuid, startDate, stopDate,
            dateMatch: startDateMatch, weeks,
            durationUnknown, orderUnknown,
            drugs: drugs.split(',').map(drug => ({
              drugName: drug
            }))
          };
          patient.treatments = (patient.treatments || []).concat([rx]);
        }
      }
      self._caches.patients = Object.values(patients);
    }
    return self._caches.patients;
  }

  static _getPatientMap(self) {
    return this.getPatientList(self).reduce((acc, pt) => {
      acc[pt.pseudoName] = pt;
      return acc;
    }, {});
  }

  static _getRefNameUuidMap(self) {
    const {references} = self.props;
    return references.reduce((acc, {refName, uuid}) => {
      acc[refName] = uuid;
      return acc;
    }, {});
  }

  static _getRawAlignmentMap(self) {
    const {rawAlignments} = self.props;
    if (!rawAlignments) {
      return {};
    }
    return rawAlignments.reduce((acc, am) => {
      acc[am.rawSequenceUuid] = am;
      return acc;
    }, {});
  }

  static getRawAlignmentMap(self) {
    return this._getRawAlignmentMap(self);
  }

  static getExportedTable(self) {
    const {rawSequences} = self.props;
    const patients = this._getPatientMap(self);
    const rawAlignmentMap = this._getRawAlignmentMap(self);
    const resultTable = [];

    const patientHistoryMap = this.getPatientHistoryMap(self);

    for (const rawSeq of rawSequences) {
      const {
        uuid, accession, ptAlias, isolateName, isolateDate,
        source,
      } = rawSeq;
      const {isChild, region} = patients[ptAlias];
      const {subtype, geneSequences} = rawAlignmentMap[uuid];
      const genes = geneSequences.map(({gene}) => gene).join(', ');
      const drms = geneSequences.map((gene) => {
        const geneName = gene.gene;
        const muts = getDRMutations(gene);
        const mutString = muts.map(
          ({shortText}) => {
            return shortText;
          }).join(',');
        return `${geneName}: ${mutString}`;
      }).join('\n');
      let mutations = [];
      // for (const {gene, mutations} of geneSequences) {
      //   for (const {shortText, primaryType} of mutations) {
      //     if (primaryType !== 'Other') {
      //       drms.push(`${gene}:${shortText}`);
      //     }
      //     // mutations.push(`${gene}:${shortText}`);
      //   }
      // }
      let regimen = '';
      let weeks = '';
      if (ptAlias in patientHistoryMap) {
        regimen = patientHistoryMap[ptAlias].drugs;
        weeks = patientHistoryMap[ptAlias].weeks;
      }

      mutations = mutations.join(', ');
      resultTable.push(new ExportedTableRow({
        accession, ptAlias, region, isChild, source, mutations,
        genes, subtype, drms, isolateName, isolateDate, regimen, weeks
      }));
    }
    return resultTable;
  }

  static getIsolateList(self) {
    if (!self._caches.isolates) {
      const {rawSequences, rawSuscResults} = self.props;
      const patients = this._getPatientMap(self);
      const rawAlignmentMap = this._getRawAlignmentMap(self);
      const refNameUuidMap = this._getRefNameUuidMap(self);
      let isolates = {};
      for (const rawSeq of rawSequences) {
        const {
          uuid, accession, ptAlias, isolateName, isolateDate,
          seqType, isolateDateMatch: dateMatch, isolateType,
          host, species, source, culture, seqTemplate, refName,
          cloneMethod, cloneName, seqMethod, authorSubtype,
          extraRefNames, parent, labSdm, labPassage,
          suscReplicaCap, suscRangeLow, suscRangeHigh
        } = rawSeq;
        const patient = patients[ptAlias];
        const rawAlignment = rawAlignmentMap[uuid];
        const refUuid = refNameUuidMap[refName];
        const extraRefUuids = (
          (extraRefNames || '').split(';').map(n => refNameUuidMap[n]).join(';')
        );
        const isMutationList = seqType === 'IncompleteSequence' || seqType === 'PartialMutationList';

        const getOrCreateIsolate = function(gene, subtype = 'Unknown', origSubtype = 'Unknown') {
          const patientIsolateNameGene = `${ptAlias}$$${isolateName}$$${gene}`;
          let isolate = isolates[patientIsolateNameGene];
          if (!isolate) {
            isolate = new Isolate({
              patientUuid: patient.uuid, refUuid, extraRefUuids, isolateName,
              isolateDate, dateMatch, isolateType, host, species, gene,
              authorSubtype, subtype, origSubtype, sequences: []
            });
            if (rawSeq.isolateType === 'Clinical') {
              isolate.clinicalIsolate = {
                source, culture, seqTemplate, cloneMethod, seqMethod
              };
            }
            else if (rawSeq.isolateType === 'Lab') {
              isolate.labIsolate = {
                parent,
                sdm: labSdm === 'Yes',
                passage: labPassage
              };
            }
            if (suscReplicaCap) {
              isolate.suscRC = {
                replicaCap: suscReplicaCap,
                rangeLow: suscRangeLow,
                rangeHigh: suscRangeHigh
              };
            }
            isolates[patientIsolateNameGene] = isolate;
          }
          isolate.suscResults = [];
          return isolate;
        };

        if (isMutationList) {
          const {gene, mutationList} = rawSeq;
          const isolate = getOrCreateIsolate(gene);
          // add sequence
          isolate.sequences.push({
            parent,
            isolateUuid: isolate.uuid,
            sequenceType: seqType,
            mutationList
          });
        }
        else if (rawAlignment) {
          const {subtype} = rawAlignment;
          const simplifiedSubtype = simplifySubtype(subtype, species);
          for (const geneSeq of rawAlignment.geneSequences) {
            const {gene} = geneSeq;
            const isolate = getOrCreateIsolate(gene, simplifiedSubtype, subtype);
            // add sequence
            addIsolateSequence(isolate, accession, seqType, cloneName, geneSeq);
          }
        }
      }
      if (rawSuscResults) {
        for (const susc of rawSuscResults) {
          const {
            ptAlias, isolateName, gene, method, drugName,
            resultMatch, result, ic, foldMatch, fold} = susc;
          const patientIsolateNameGene = `${ptAlias}$$${isolateName}$$${gene}`;
          const isolate = isolates[patientIsolateNameGene];
          const {suscResults} = isolate;
          suscResults.push({
            method, drugName, resultMatch,
            result, ic, foldMatch, fold});
          isolate.suscResults = suscResults;
        }
      }

      isolates = Object.values(isolates);
      for (const isolate of isolates) {
        // the isolate is not a clone, remove cloneName
        if (isolate.sequences.length === 1) {
          const [seq] = isolate.sequences;
          // if (isolate.isolateType === 'Clinical') {
          //   isolate.clinicalIsolate.cloneMethod = 'None';
          // }
          seq.cloneName = null;
        }
      }
      self._caches.isolates = isolates;
    }
    return self._caches.isolates;
  }

}



// WEBPACK FOOTER //
// ./src/models/raw-data-collection.js